import React, { useState, useReducer, useCallback } from "react";
import { FiCalendar } from "react-icons/fi";
import { useQuery, useMutation, useQueryClient } from "react-query";
import MoonLoader from "react-spinners/MoonLoader";
import { FaRegNoteSticky } from "react-icons/fa6";
import { SiGooglemeet } from "react-icons/si";
import { BsCalendarDateFill } from "react-icons/bs";

import SlotsSelector from "../../BookConsultant/components/SlotsSelector";
import MainButton from "../../../components/customButtons/MainButton";
import OutlineButton from "../../../components/customButtons/OutlineButton";
import Alert from "../../../components/alert";
import useSnackbar from "../../../components/snackbar/hooks/useSnackbar";
import Modal from "../../../components/modal/index";
import {
  getNextConsultations,
  getWaitingOnsiteRequests,
  cancelNextConsultation,
  rescheduleNextConsultation,
  requestOnsiteReschedule,
} from "../../../apis/profileApi";
import {
  NEXT_CONSULTATION,
  WAITING_ONSITE_REQUESTS,
} from "../../../queries-keys/consultation";
import { getErrorMessage } from "../../../utils/errors";
import { formatDateToTime, formatDateToddMMyyyy } from "../../../utils/dates";
import { CONSULTATION_TYPES } from "../../../constants/consultationService";
import useGetAdminConfigs from "../../../hooks/useGetAdminConfigs";
import EmptyState from "../../../components/emptyState";

const SET_BOOKING_DATE = "SET_BOOKING_DATE";
const SET_BOOKING_TIME_SLOT = "SET_BOOKING_TIME_SLOT";
const RESET_BOOKING_DETAILS = "RESET_BOOKING_DETAILS";

const reBookingInitialState = {
  type: undefined,
  date: undefined,
  timeSlot: undefined,
};

const reBookingReducer = (state, action) => {
  switch (action.type) {
    case SET_BOOKING_DATE:
      return { ...state, date: action.payload };

    case SET_BOOKING_TIME_SLOT:
      return { ...state, timeSlot: action.payload };

    case RESET_BOOKING_DETAILS:
      return reBookingInitialState;

    default:
      return state;
  }
};

const isOnsite = (consultation) =>
  consultation?.type === CONSULTATION_TYPES.ONSITE.name;

const formatWhen = (date) =>
  `${formatDateToddMMyyyy(date)}, ${formatDateToTime(date)}`;

const TITLES = {
  [CONSULTATION_TYPES.ONLINE.name]: "Online consultation",
  [CONSULTATION_TYPES.IN_OFFICE.name]: "In-office consultation",
  [CONSULTATION_TYPES.ONSITE.name]: "Onsite visit",
};

// Lists every upcoming consultation and the onsite requests waiting for our
// team, with reschedule and cancel actions
const NextConsultation = () => {
  // { type: "reschedule" | "onsiteReschedule" | "cancel" | "notes", consultation }
  const [openAction, setOpenAction] = useState();
  const closeAction = useCallback(() => setOpenAction(undefined), []);
  const { handleOpenSnackbar } = useSnackbar();

  const { configs } = useGetAdminConfigs();
  const limits = {
    reschedules: configs.numberOfReschedulingTime ?? 3,
    rescheduleHours: configs.numberOfHoursNotToRescheduleBefore ?? 24,
    cancelHours: configs.numberOfHoursNotToCancelBefore ?? 24,
  };

  const { data: consultations = [], isFetching: isFetchingConsultations } =
    useQuery(NEXT_CONSULTATION(), getNextConsultations, {
      onError: (error) => handleOpenSnackbar(getErrorMessage(error), "error"),
      refetchOnWindowFocus: false,
    });

  const { data: waitingRequests = [], isFetching: isFetchingWaiting } =
    useQuery(WAITING_ONSITE_REQUESTS(), getWaitingOnsiteRequests, {
      onError: (error) => handleOpenSnackbar(getErrorMessage(error), "error"),
      refetchOnWindowFocus: false,
    });

  const isLoading =
    (isFetchingConsultations || isFetchingWaiting) &&
    !consultations.length &&
    !waitingRequests.length;
  const selected = openAction?.consultation;

  return (
    <>
      {/* Mounted only while open: a hidden Modal still closes on outside clicks */}
      {openAction?.type === "notes" && (
        <ConsultationNotesModal notes={selected?.notes} show onClose={closeAction} />
      )}
      {openAction?.type === "reschedule" && (
        <ReschedulingModal consultation={selected} show onClose={closeAction} />
      )}
      {openAction?.type === "onsiteReschedule" && (
        <OnsiteRescheduleModal consultation={selected} show onClose={closeAction} />
      )}
      {openAction?.type === "cancel" && (
        <CancelConsultationModal consultation={selected} show onClose={closeAction} />
      )}

      <div className="relative flex h-fit w-full flex-grow flex-col space-y-3 overflow-y-hidden rounded border-[0.3px] border-softGray p-3 shadow shadow-softGray lg:p-5">
        <span className="font-medium">Upcoming Consultations</span>
        <div className="flex h-full flex-col space-y-3 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <MoonLoader color="#24595C" />
            </div>
          ) : consultations.length || waitingRequests.length ? (
            <>
              {waitingRequests.map((request) => (
                <WaitingOnsiteRequestCard key={`request-${request.id}`} request={request} />
              ))}
              {consultations.map((consultation) => (
                <ConsultationCard
                  key={consultation.id}
                  consultation={consultation}
                  limits={limits}
                  onAction={(type) => setOpenAction({ type, consultation })}
                />
              ))}
            </>
          ) : (
            <EmptyState
              icon={FiCalendar}
              title="No upcoming consultations"
              description="Consultations you book will show up here."
              className="h-full"
            />
          )}
        </div>
      </div>
    </>
  );
};

const rescheduleBlockedReason = (consultation, limits) => {
  if (consultation.canBeRescheduled) return undefined;
  if (consultation.reschedulesLeft === 0) {
    return `You've used all ${limits.reschedules} reschedules for this consultation.`;
  }
  return `Changing the time closes ${limits.rescheduleHours} hours before your consultation.`;
};

const cancelBlockedReason = (consultation, limits) =>
  consultation.canBeCancelled
    ? undefined
    : `Cancellation closes ${limits.cancelHours} hours before your consultation.`;

const ConsultationCard = ({ consultation, limits, onAction }) => {
  const onsite = isOnsite(consultation);
  const reasons = [
    rescheduleBlockedReason(consultation, limits),
    cancelBlockedReason(consultation, limits),
  ].filter((reason, index, all) => reason && all.indexOf(reason) === index);

  return (
    <div className="flex flex-col space-y-2 rounded border-[0.3px] border-softGray p-3">
      <div className="flex items-start justify-between">
        <span className="font-bold">{TITLES[consultation.type] || consultation.type}</span>
        <div className="flex text-[22px] text-primary">
          {/* External links: open in a new tab, not through the router */}
          {consultation.type === CONSULTATION_TYPES.ONLINE.name &&
            consultation.meetingLink && (
              <a
                href={consultation.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                title="Join the meeting"
                className="cursor-pointer rounded p-1 hover:bg-gray-200"
              >
                <SiGooglemeet />
              </a>
            )}
          {consultation.addToCalendarUrl && (
            <a
              href={consultation.addToCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Add to your calendar"
              className="cursor-pointer rounded p-1 hover:bg-gray-200"
            >
              <BsCalendarDateFill />
            </a>
          )}
        </div>
      </div>
      <div className="flex flex-col space-y-1 text-[14px]">
        <Detail label="When">{formatWhen(consultation.start)}</Detail>
        <Detail label="Consultant">
          {consultation.consultantFirstName} {consultation.consultantLastName}
          {consultation.consultantMobile && ` (${consultation.consultantMobile})`}
        </Detail>
        {onsite && consultation.address && (
          <Detail label="Address">{consultation.address}</Detail>
        )}
        <Detail label="Reschedules left">
          {`${consultation.reschedulesLeft} of ${limits.reschedules}`}
        </Detail>
        {consultation.notes && (
          <div
            className="group flex w-fit cursor-pointer items-center space-x-2"
            onClick={() => onAction("notes")}
          >
            <FaRegNoteSticky size={18} />
            <span className="underline-offset-2 group-hover:underline">View Notes</span>
          </div>
        )}
      </div>
      {reasons.map((reason) => (
        <span key={reason} className="text-[13px] text-highlight">
          * {reason}
        </span>
      ))}
      <div className="flex space-x-3">
        <MainButton
          className="flex-1 text-[14px]"
          onClick={() => onAction(onsite ? "onsiteReschedule" : "reschedule")}
          disabled={!consultation.canBeRescheduled}
        >
          {onsite ? "Request another time" : "Reschedule"}
        </MainButton>
        <OutlineButton
          className="flex-1 text-[14px]"
          onClick={() => onAction("cancel")}
          disabled={!consultation.canBeCancelled}
        >
          {onsite ? "Cancel visit" : "Cancel"}
        </OutlineButton>
      </div>
    </div>
  );
};

const WAITING_TEXT = {
  reschedule_requested:
    "You asked for another time. Our team will contact you to arrange a new visit.",
};

const WaitingOnsiteRequestCard = ({ request }) => (
  <div className="flex flex-col space-y-1 rounded border-[0.3px] border-softGray bg-gray-50 p-3 text-[14px]">
    <span className="font-bold">Onsite visit requested</span>
    <Detail label="Address">{request.address}</Detail>
    {request.paid_at && <Detail label="Paid on">{formatDateToddMMyyyy(request.paid_at)}</Detail>}
    <span className="text-secondary">
      {WAITING_TEXT[request.last_user_action] ||
        "Our team will contact you soon to schedule your visit."}
    </span>
  </div>
);

const Detail = ({ label, children }) => (
  <div className="flex space-x-2">
    <span className="font-medium">{label}:</span>
    <span>{children}</span>
  </div>
);

// Refetch everything a booking change can affect
const useRefreshConsultations = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(NEXT_CONSULTATION());
    queryClient.invalidateQueries(WAITING_ONSITE_REQUESTS());
    queryClient.invalidateQueries(["slots"]);
  };
};

const ReschedulingModal = ({ consultation, show, onClose }) => {
  const { handleOpenSnackbar } = useSnackbar();
  const refresh = useRefreshConsultations();
  // Mounted per opening, so each starts with an empty selection
  const [reBookingDetails, reBookingDispatch] = useReducer(reBookingReducer, {
    ...reBookingInitialState,
    type: consultation?.type,
  });
  const [error, setError] = useState();
  const close = onClose;

  // Only what the server accepts: same type, not pricier, not the current slot
  const filterSlot = useCallback(
    (slot) =>
      slot.id !== consultation?.id && (slot.price ?? 0) <= (consultation?.price ?? 0),
    [consultation?.id, consultation?.price],
  );

  const { mutate: reschedule, isLoading: isRescheduling } = useMutation(
    (newSlotId) =>
      rescheduleNextConsultation({ oldSlotId: consultation?.id, newSlotId }),
    {
      onSuccess: (newSlot) => {
        refresh();
        close();
        handleOpenSnackbar(
          `Rescheduled to ${formatWhen(newSlot?.start)}. We've emailed you the new details.`,
          "success",
        );
      },
      onError: (requestError) => {
        // Stay open so the user can pick another time
        setError(getErrorMessage(requestError));
        reBookingDispatch({ type: SET_BOOKING_TIME_SLOT });
        refresh();
      },
    },
  );

  return (
    <Modal show={show} onClose={close}>
      <div className="flex flex-col space-y-3 text-primary">
        <span className="text-[18px] font-bold">Choose a new time</span>
        {consultation && (
          <span className="text-[14px] text-secondary">
            Current booking: {formatWhen(consultation.start)}. You can move to another{" "}
            {consultation.type} slot at the same or a lower price.
          </span>
        )}
        <Alert isOpen={!!error} message={error} severity="error" />
        <div className="flex max-h-[420px] flex-col space-y-2 overflow-y-auto pr-2">
          <SlotsSelector
            bookingDetails={reBookingDetails}
            bookingDispatch={(action) => {
              setError(undefined);
              reBookingDispatch(action);
            }}
            filterSlot={filterSlot}
          />
        </div>
        <MainButton
          className="mx-auto w-full lg:max-w-[300px]"
          onClick={() => reschedule(reBookingDetails?.timeSlot?.id)}
          isLoading={isRescheduling}
          disabled={!reBookingDetails.date || !reBookingDetails.timeSlot}
        >
          Confirm new time
        </MainButton>
      </div>
    </Modal>
  );
};

const OnsiteRescheduleModal = ({ consultation, show, onClose }) => {
  const { handleOpenSnackbar } = useSnackbar();
  const refresh = useRefreshConsultations();
  const [error, setError] = useState();

  const close = onClose;

  const { mutate: requestReschedule, isLoading } = useMutation(
    () =>
      requestOnsiteReschedule({
        oldSlotId: consultation?.id,
        onsiteVisitRequestId: consultation?.onsiteVisitRequestId,
      }),
    {
      onSuccess: (response) => {
        refresh();
        close();
        handleOpenSnackbar(
          response?.message ||
            "We received your request. Our team will contact you to arrange a new visit.",
          "success",
        );
      },
      onError: (requestError) => setError(getErrorMessage(requestError)),
    },
  );

  return (
    <Modal show={show} onClose={close}>
      <div className="flex flex-col space-y-3 text-primary">
        <span className="text-[18px] font-bold">Request another time</span>
        {consultation && (
          <p className="text-[14px]">
            Your visit on <strong>{formatWhen(consultation.start)}</strong> will be
            cancelled, and our team will contact you to arrange a new time. This counts
            as one of your reschedules ({consultation.reschedulesLeft} left).
          </p>
        )}
        <Alert isOpen={!!error} message={error} severity="error" />
        <div className="flex space-x-5">
          <OutlineButton onClick={close} className="flex-1">
            Keep my visit
          </OutlineButton>
          <MainButton
            onClick={() => requestReschedule()}
            isLoading={isLoading}
            className="flex-1"
          >
            Request new time
          </MainButton>
        </div>
      </div>
    </Modal>
  );
};

const CancelConsultationModal = ({ consultation, show, onClose }) => {
  const { handleOpenSnackbar } = useSnackbar();
  const refresh = useRefreshConsultations();
  const [understandNotRefundable, setUnderstandNotRefundable] = useState(false);
  const [error, setError] = useState();
  const onsite = isOnsite(consultation);

  const close = onClose;

  const { mutate: cancel, isLoading: isCancelling } = useMutation(
    () => cancelNextConsultation(consultation?.id),
    {
      onSuccess: () => {
        const when = formatWhen(consultation?.start);
        refresh();
        close();
        handleOpenSnackbar(
          `Your ${onsite ? "visit" : "consultation"} on ${when} was cancelled. A confirmation email is on its way.`,
          "success",
        );
      },
      onError: (requestError) => {
        setError(getErrorMessage(requestError));
        refresh();
      },
    },
  );

  return (
    <Modal show={show} onClose={close}>
      <div className="flex flex-col space-y-3 text-primary">
        <span className="text-[18px] font-bold">
          {onsite ? "Cancel onsite visit" : "Cancel consultation"}
        </span>
        {consultation && (
          <p className="text-[14px]">
            You're about to cancel your {onsite ? "visit" : "consultation"} on{" "}
            <strong>{formatWhen(consultation.start)}</strong>.
          </p>
        )}
        <div className="text-[14px]">
          <p className="text-highlight">
            Cancelled {onsite ? "visits" : "consultations"} are not refunded.
          </p>
          <label className="flex items-center">
            <input
              type="checkbox"
              className="mr-2"
              checked={understandNotRefundable}
              onChange={() => setUnderstandNotRefundable((prev) => !prev)}
            />
            I understand this is not refundable
          </label>
        </div>
        <Alert isOpen={!!error} message={error} severity="error" />
        <div className="flex space-x-5">
          <OutlineButton onClick={close} className="flex-1">
            {onsite ? "Keep my visit" : "Keep my booking"}
          </OutlineButton>
          <MainButton
            onClick={() => cancel()}
            isLoading={isCancelling}
            disabled={!understandNotRefundable}
            className="flex-1"
          >
            {onsite ? "Cancel visit" : "Cancel consultation"}
          </MainButton>
        </div>
      </div>
    </Modal>
  );
};

const ConsultationNotesModal = ({ notes, show, onClose }) => {
  return (
    <Modal show={show} onClose={onClose}>
      <div className="max-h-[400px] overflow-y-auto pr-2">
        <p className="whitespace-pre-wrap">{notes}</p>
      </div>
    </Modal>
  );
};

export default NextConsultation;
